import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import BaseSelect from 'components/common/BaseSelect/BaseSelect';
import PropTypes from 'prop-types';
import * as extract from 'extractFromState';
import * as searchActions from 'constants/searchActions';
import * as tools from 'selectors/tools';
import { handleError } from 'services/ErrorService/ErrorService';
import { setSelectedCustomerGroupId } from 'actions/tools/OrdersSearchActions';
import { assignToCustomerGroup } from 'actions/tools/DecisionTree/SimplrOrderActions';
import { simplrOrderGetStaticCustomerGroups } from 'actions/tools/SimplrOrderActions';
import { returnToPreviousMacroWizardPath, addEdgeToDecisionTreePath } from 'actions/searchActions';
import Loading from 'components/LoadingComponent/LoadingComponent';
import { BackButton, NextButton } from 'components/common/buttonsComponents';
import { formatFromSearchType } from 'util/formatUtil';
import DecisionTreeInfoWidget from '../../DTInfoWidget/DTInfoWidget';
import {
  Wrapper,
  Content,
  EmailInput,
  Title,
  SubText,
  TextSection,
  InfoSection,
  ButtonSection,
} from './DTSetCustomerGroupNode.styles';

const DTSetCustomerGroupNode = (props) => {
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');

  // TODO: Fix dependency array
  useEffect(() => {
    props.setSelectedCustomerGroupId(null);
    handleGetCustomerGroups();
    setEmail(props.defaultSearchValue);
  }, []); // eslint-disable-line

  const handleGetCustomerGroups = async () => {
    try {
      const { domain, brandName } = getBrandNameAndDomain();
      await props.simplrOrderGetStaticCustomerGroups(props.simplrAccountId, {
        domain,
        brandName,
      });
    } catch (error) {
      handleError({ error, customMessage: 'There was a problem loading customer groups.', showPopup: true });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignToCustomerGroup = async () => {
    try {
      const { domain, brandName } = getBrandNameAndDomain();

      const query = {
        domain,
        brandName,
        email: email.trim(),
        customerGroupId: props.selectedCustomerGroupId,
      };

      setLoading(true);
      await props.assignToCustomerGroup(props.simplrAccountId, query);
    } catch (error) {
      handleError({ error, customMessage: 'There was a problem assigning the customer group.', showPopup: true });
    } finally {
      setLoading(false);
    }
  };

  const handleBackButton = () => {
    props.returnToPreviousMacroWizardPath();
  };

  const handleSelectGroup = (option) => {
    if (option) {
      props.setSelectedCustomerGroupId(option.value);
    } else {
      props.setSelectedCustomerGroupId(null);
    }
  };

  const getDisplayValue = () => {
    const { defaultSearchValue } = props;
    const shouldMaskValue = email === defaultSearchValue;

    return shouldMaskValue ? formatFromSearchType(searchActions.SALESFORCE_ORDER_EMAIL, email) : email;
  };

  const getBrandNameAndDomain = () => {
    const { simplrorderConfig = {} } = props.selectedToolDomains;
    const domain = Object.keys(simplrorderConfig)[0];
    const brandName = simplrorderConfig[domain] && simplrorderConfig[domain].brandName;

    return {
      domain,
      brandName,
    };
  };

  const getCustomerGroupOptions = () => {
    const options = props.customerGroups || [];

    return options.map((group) => ({
      value: group.customerGroupId,
      label: group.customerGroupId,
    }));
  };

  const shouldDisableNextButton = () => {
    return !props.selectedCustomerGroupId || !email || !props.selectedTicketTaken;
  };

  const renderTitleAndSubText = () => {
    const { decisionNode } = props;

    if (!decisionNode) {
      return null;
    }

    if (decisionNode.subText || decisionNode.title) {
      return (
        <TextSection>
          {decisionNode.title && <Title>{decisionNode.title}</Title>}
          {decisionNode.subText && <SubText>{decisionNode.subText}</SubText>}
        </TextSection>
      );
    }

    return null;
  };

  const { decisionNode } = props;

  if (decisionNode) {
    if (loading) {
      return <Loading />;
    }

    return (
      <Wrapper>
        <InfoSection>{!!decisionNode.infoWidget && <DecisionTreeInfoWidget />}</InfoSection>
        <Content reduceTopPadding={!!decisionNode.infoWidget}>
          {renderTitleAndSubText()}
          <BaseSelect
            isClearable
            label={'Select a group'}
            placeholder={'Select a group'}
            value={props.selectedCustomerGroupId}
            options={getCustomerGroupOptions()}
            onChange={(option) => handleSelectGroup(option)}
          />
          <EmailInput
            value={getDisplayValue()}
            placeholder={'Customer email'}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Content>
        <ButtonSection>
          <BackButton onClick={() => handleBackButton()}>BACK</BackButton>
          <NextButton
            disabled={shouldDisableNextButton()}
            active={!shouldDisableNextButton()} // TO DO: "active" state is dumb
            onClick={() => handleAssignToCustomerGroup()}
          >
            NEXT
          </NextButton>
        </ButtonSection>
      </Wrapper>
    );
  }

  return null;
};

DTSetCustomerGroupNode.propTypes = {
  decisionNode: PropTypes.object.isRequired,
  simplrAccountId: PropTypes.string.isRequired,
  selectedToolDomains: PropTypes.object.isRequired,
  defaultSearchValue: PropTypes.string.isRequired,
  returnToPreviousMacroWizardPath: PropTypes.func.isRequired,
  simplrOrderGetStaticCustomerGroups: PropTypes.func.isRequired,
  assignToCustomerGroup: PropTypes.func.isRequired,
  addEdgeToDecisionTreePath: PropTypes.func.isRequired,
  customerGroups: PropTypes.array.isRequired,
  selectedCustomerGroupId: PropTypes.string.isRequired,
  setSelectedCustomerGroupId: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  decisionNode: extract.getDecisionNodeAtPath(state),
  simplrAccountId: extract.getSimplrAccountIdOfSelectedTicket(state),
  selectedToolDomains: state.tools.general.selectedToolDomains,
  defaultSearchValue: tools.getDefaultEmailSearchValue(state),
  customerGroups: extract.extractCustomerGroups(state),
  selectedCustomerGroupId: extract.extractSelectedCustomerGroupId(state),
  selectedTicketTaken: extract.isSelectedTicketTaken(state),
});

const mapStateToDispatch = {
  returnToPreviousMacroWizardPath,
  simplrOrderGetStaticCustomerGroups,
  assignToCustomerGroup,
  addEdgeToDecisionTreePath,
  setSelectedCustomerGroupId,
};

export default connect(mapStateToProps, mapStateToDispatch)(DTSetCustomerGroupNode);
